import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BoService } from '../services/bo.service';
import { ListeControle, ListStatePatch, formatValidUntil } from './mes-listes.model';

type StatusFilter = 'tous' | 'marques' | 'actif' | 'inactif' | 'expires' | 'archivees' | 'masquees';
type DatePreset = '7j' | '30j' | 'mois' | 'annee' | 'custom' | null;
type ViewMode = 'list' | 'grid';

/**
 * Slide-over "Mes listes de contrôle" — refonte (ticket wilout/issues#4956).
 * Design : Variation A. Remplace l'overlay jQuery legacy de app.component.
 */
@Component({
  selector: 'app-mes-listes-controle',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mes-listes-controle.component.html',
  styleUrls: ['./mes-listes-controle.component.css'],
})
export class MesListesControleComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();

  lists: ListeControle[] = [];
  order: string[] = [];
  loading = true;

  query = '';
  statusFilter: StatusFilter = 'tous';
  statusMenuOpen = false;
  dateFilterOpen = false;
  datePreset: DatePreset = null;
  dateFrom = '';
  dateTo = '';
  viewMode: ViewMode = 'list';
  expiredOpen = false;

  selectedId: string | null = null;
  copiedId: string | null = null;

  draggingId: string | null = null;
  overId: string | null = null;
  busyId: string | null = null;
  duplicatingId: string | null = null;
  /** Liste dont la dernière duplication a échoué : signalée sur sa carte jusqu'au prochain essai. */
  duplicateErrorId: string | null = null;

  readonly formatValidUntil = formatValidUntil;
  readonly statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'tous', label: 'Tous' },
    { value: 'marques', label: 'Marquées' },
    { value: 'actif', label: 'Actives' },
    { value: 'inactif', label: 'Inactives' },
    { value: 'expires', label: 'Expirées' },
    { value: 'masquees', label: 'Masquées' },
    { value: 'archivees', label: 'Archivées' },
  ];

  constructor(private bo: BoService) {}

  ngOnInit(): void {
    try {
      const current = JSON.parse(localStorage.getItem('dataList') || '{}');
      this.selectedId = current && current['_id'] ? current['_id'] : null;
    } catch (e) { console.warn('[mes-listes] dataList parse échoué', e); this.selectedId = null; }
    this.load();
  }

  load(): void {
    this.loading = true;
    this.bo.fetchListsModel()
      .then(lists => {
        this.lists = lists;
        this.order = [...lists]
          .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
          .map(l => l.id);
        this.loading = false;
      })
      .catch((e) => { console.warn('[mes-listes] chargement des listes échoué', e); this.loading = false; });
  }

  // ----- Dérivés (pipeline filtre + tri) -----

  get items(): ListeControle[] {
    const byId = new Map(this.lists.map(l => [l.id, l]));
    let list = this.order.map(id => byId.get(id)).filter((x): x is ListeControle => !!x);

    if (this.statusFilter === 'archivees') {
      list = list.filter(x => x.archived);
    } else if (this.statusFilter === 'masquees') {
      list = list.filter(x => x.hidden && !x.archived);
    } else {
      list = list.filter(x => !x.archived && !x.hidden);
      if (this.statusFilter === 'marques') list = list.filter(x => x.flag);
      else if (this.statusFilter === 'actif') list = list.filter(x => x.status === 'active');
      else if (this.statusFilter === 'inactif') list = list.filter(x => x.status === 'inactive');
      else if (this.statusFilter === 'expires') list = list.filter(x => x.status === 'expired');
    }

    const q = this.query.trim().toLowerCase();
    if (q) list = list.filter(x => x.name.toLowerCase().includes(q) || x.id.toLowerCase().includes(q));

    if (this.datePreset || this.dateFrom || this.dateTo) {
      const { from, to } = this.dateRange();
      list = list.filter(x => {
        const d = this.parseValidUntil(x.validUntil);
        if (!d) return true;
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }
    return list;
  }

  /** Vrai si une recherche est active → on déplie les expirées pour ne pas masquer de résultats. */
  get searchActive(): boolean { return this.query.trim().length > 0; }

  get grouped(): boolean { return this.statusFilter === 'tous'; }
  get activeItems(): ListeControle[] { return this.grouped ? this.items.filter(x => x.status !== 'expired') : this.items; }
  get expiredItems(): ListeControle[] { return this.grouped ? this.items.filter(x => x.status === 'expired') : []; }

  get activeFiltersCount(): number {
    const dateActive = this.datePreset || this.dateFrom || this.dateTo;
    return (dateActive ? 1 : 0) + (this.statusFilter !== 'tous' ? 1 : 0);
  }
  get archivedCount(): number { return this.lists.filter(l => l.archived).length; }
  get hiddenCount(): number { return this.lists.filter(l => l.hidden && !l.archived).length; }

  get statusLabel(): string {
    return this.statusOptions.find(o => o.value === this.statusFilter)?.label ?? '';
  }

  /** Compteur affiché en suffixe pour les filtres masquées/archivées (0 = pas de suffixe). */
  optionCount(value: StatusFilter): number {
    if (value === 'masquees') return this.hiddenCount;
    if (value === 'archivees') return this.archivedCount;
    return 0;
  }

  private dateRange(): { from: Date | null; to: Date | null } {
    const now = new Date();
    let from: Date | null = null;
    let to: Date | null = null;
    if (this.datePreset === '7j') { from = new Date(now); from.setDate(now.getDate() - 7); }
    else if (this.datePreset === '30j') { from = new Date(now); from.setDate(now.getDate() - 30); }
    else if (this.datePreset === 'mois') { from = new Date(now.getFullYear(), now.getMonth(), 1); }
    else if (this.datePreset === 'annee') { from = new Date(now.getFullYear(), 0, 1); }
    else if (this.datePreset === 'custom') {
      from = this.dateFrom ? new Date(this.dateFrom) : null;
      to = this.dateTo ? new Date(this.dateTo) : null;
    }
    return { from, to };
  }

  /** Parse 'DD-MM-YYYY HH:mm:ss' en Date. */
  private parseValidUntil(value: string | null): Date | null {
    if (!value || value.length < 10) return null;
    const [datePart, timePart] = value.split(' ');
    const [d, m, y] = datePart.split('-').map(Number);
    const [h, min, s] = (timePart ?? '0:0:0').split(':').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, h || 0, min || 0, s || 0);
  }

  // ----- Actions UI -----

  setStatus(s: StatusFilter): void { this.statusFilter = s; this.statusMenuOpen = false; }
  toggleView(): void { this.viewMode = this.viewMode === 'list' ? 'grid' : 'list'; }
  close(): void { this.closed.emit(); }

  clearQuery(): void { this.query = ''; }
  resetFilters(): void { this.query = ''; this.statusFilter = 'tous'; this.clearDate(); }

  setDatePreset(p: DatePreset): void { this.datePreset = p; this.dateFrom = ''; this.dateTo = ''; }
  onDateFromChange(v: string): void { this.dateFrom = v; this.datePreset = 'custom'; }
  onDateToChange(v: string): void { this.dateTo = v; this.datePreset = 'custom'; }
  clearDate(): void { this.datePreset = null; this.dateFrom = ''; this.dateTo = ''; }

  copyId(item: ListeControle, ev: Event): void {
    ev.stopPropagation();
    navigator.clipboard?.writeText(item.id);
    this.copiedId = item.id;
    setTimeout(() => { if (this.copiedId === item.id) this.copiedId = null; }, 1500);
  }

  select(item: ListeControle): void {
    if (this.busyId) return;
    this.busyId = item.id;
    this.selectedId = item.id;
    // Fermer immédiatement (dans la zone Angular, depuis l'event click) avant la
    // navigation : sinon le close() post-navigation tourne hors zone → pas de CD.
    this.close();
    this.bo.selectListRaw(item.raw).finally(() => { this.busyId = null; });
  }

  toggleFlag(item: ListeControle, ev: Event): void { this.toggleBoolState(item, 'flag', ev); }
  toggleHidden(item: ListeControle, ev: Event): void { this.toggleBoolState(item, 'hidden', ev); }
  toggleArchive(item: ListeControle, ev: Event): void { this.toggleBoolState(item, 'archived', ev); }

  /** Bascule optimiste d'un état booléen persisté (flag/hidden/archived) avec rollback si l'API échoue. */
  private toggleBoolState(item: ListeControle, prop: 'flag' | 'hidden' | 'archived', ev: Event): void {
    ev.stopPropagation();
    item[prop] = !item[prop];
    const patch: ListStatePatch = {};
    patch[prop] = item[prop];
    this.bo.setListState(item.id, patch).catch((e) => {
      console.warn(`[mes-listes] échec persistance ${prop}, rollback`, e);
      item[prop] = !item[prop];
    });
  }
  /** Duplique la liste (#5420) puis recharge pour afficher la copie ; un clic à la fois. */
  async duplicate(item: ListeControle, ev: Event): Promise<void> {
    ev.stopPropagation();
    if (this.duplicatingId) return;
    this.duplicatingId = item.id;
    this.duplicateErrorId = null;
    try {
      await this.bo.duplicateList(item.id);
      this.load();
    } catch (e) {
      console.warn('[mes-listes] échec de la duplication', e);
      this.duplicateErrorId = item.id;
    } finally {
      this.duplicatingId = null;
    }
  }

  // ----- Drag & drop reorder -----

  onDragStart(item: ListeControle): void { this.draggingId = item.id; }
  onDragOver(item: ListeControle, ev: DragEvent): void { ev.preventDefault(); this.overId = item.id; }
  onDrop(target: ListeControle): void {
    const dragging = this.draggingId;
    this.draggingId = null;
    this.overId = null;
    if (!dragging || dragging === target.id) return;
    const next = [...this.order];
    const from = next.indexOf(dragging);
    const to = next.indexOf(target.id);
    if (from < 0 || to < 0) return;
    next.splice(from, 1);
    next.splice(to, 0, dragging);
    this.order = next;
    this.bo.reorderLists(next).catch(() => { /* l'ordre local reste, retry au prochain load */ });
  }
  onDragEnd(): void { this.draggingId = null; this.overId = null; }

  trackById(_: number, item: ListeControle): string { return item.id; }
}
